import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import {
    defaultDatasetLineChartConfig,
    defaultLoadConf
} from "components/dashboard/data-input-output-table/defaultConfigs";
import React, {FunctionComponent, useEffect, useMemo, useState} from "react";
import { ICommonComponentEditorProps } from "components/ComponentType";
import { DatasetChartProps } from "../interfaces";

import { DatasetMultiSelect } from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import LineEditor from "./line-editor/LineEditor";


function DatasetLineChartEditor({ config, onConfigChange }: ICommonComponentEditorProps) {
    let conf: DatasetChartProps = config ?? defaultDatasetLineChartConfig;

    if (Object.keys(conf).length === 0) {
        conf = defaultDatasetLineChartConfig;
    }

    const [currentConfig, setCurrentConfig] = useState(conf);
    const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
    const [lineEditorDialogVisible, setLineEditorDialogVisibility] = useState<boolean>(false);

    const updatedSelectedDatasets = useMemo(() => {
        const newSelectedDatasets = [...selectedDatasets];

        conf.identifiers?.forEach((identifier) => {
            if (!newSelectedDatasets.includes(identifier)) {
                newSelectedDatasets.push(identifier);
            }
        });

        conf.datasets?.forEach((dataset) => {
            if (!newSelectedDatasets.includes(dataset.identifier)) {
                newSelectedDatasets.push(dataset.identifier);
            }
        });

        return newSelectedDatasets;
    }, [conf.identifiers, conf.datasets]);

    useEffect(() => {
        setSelectedDatasets(updatedSelectedDatasets);
    }, [updatedSelectedDatasets]);

    useEffect(() => {
        const tempIdentifiers = currentConfig.datasets?.map((dataset) => dataset.identifier);
        if (tempIdentifiers) {
            setConfigPropertyState("identifiers", tempIdentifiers);
        }
    }, [currentConfig.datasets]);

    const setConfigPropertyState = (property: keyof DatasetChartProps, value: any) => {
        const newConfig = { ...currentConfig, [property]: value };

        onConfigChange(newConfig);
        setCurrentConfig(newConfig);
    };

    const handleConfigChange = (e: any): void => {
        setConfigPropertyState(e.target.id as keyof DatasetChartProps, e.target.value);
    };

    const handleDatasetChange = (selectedIdentifiers: string[]): void => {
        setSelectedDatasets(selectedIdentifiers);

        const selectedDatasetsArray = selectedIdentifiers.map((identifier) => {
            const oldDataset = currentConfig.datasets ? currentConfig.datasets?.find((dataset) => dataset.identifier === identifier) : undefined;
            if (oldDataset) {
                return oldDataset;
            } else {
                return {
                    identifier,
                };
            }
        });

        setConfigPropertyState("datasets", selectedDatasetsArray);
    };

    const handleLoadConf = (e: any): void => {
        const tempConfig = structuredClone(currentConfig);
        if (e.value) {
            tempConfig.loadConf = defaultLoadConf;
        } else {
            delete tempConfig.loadConf;
        }
        onConfigChange(tempConfig);
        setCurrentConfig(tempConfig);
    };

    const handleLoadConfChange = (e: any): void => {
        const tempConfig = structuredClone(currentConfig);
        console.log(e)
        tempConfig.loadConf![e.target.id] = e.target.value;
        console.log(tempConfig)
        onConfigChange(tempConfig);
        setCurrentConfig(tempConfig);
    }

    const handleEditLinesClick = (): void => {
            const tempConfig = structuredClone(currentConfig);
            currentConfig.identifiers?.map((identifier) => {
                if(tempConfig.datasets === undefined) {
                    tempConfig.datasets = [];
                }
                if (!tempConfig.datasets?.find((dataset) => dataset.identifier === identifier)) {
                    tempConfig.datasets?.push({
                        identifier,
                    });
                }
            }
            );

            onConfigChange(tempConfig);
            setCurrentConfig(tempConfig);
            setLineEditorDialogVisibility(true);
    }

    return (
        <div className="grid p-fluid">
            <div className="col-12 flex flex-column mb-3">
                <label htmlFor="identifiers">Datasets</label>
                <div className="flex">
                    <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
                    <Button
                        icon="pi pi-pencil"
                        type="button"
                        className="p-button-secondary"
                        style={{ marginLeft: "10px" }}
                        tooltip="Edit Lines"
                        onClick={() => handleEditLinesClick()}
                    />
                    <Dialog
                        onHide={() => setLineEditorDialogVisibility(false)}
                        visible={lineEditorDialogVisible}
                        header="Edit Line style"
                        style={{ width: "min(100vw, 650px)", height: "400x" }}
                    >
                        <LineEditor
                            currentConfig={currentConfig}
                            setCurrentConfig={setCurrentConfig}
                            onConfigChange={onConfigChange}
                        />
                    </Dialog>
                </div>
            </div>
            <div className="col-2 flex flex-column mb-3">
                <label htmlFor="height">Height</label>
                <div className="p-inputgroup w-full">
                    <InputNumber
                        id="height"
                        value={currentConfig.height as number}
                        onValueChange={handleConfigChange}
                    />
                    <span className="p-inputgroup-addon">px</span>
                </div>
            </div>
            <div className="col-2 flex flex-column mb-3">
                <label htmlFor="updateFrequency">Update Frequency</label>
                <div className="p-inputgroup w-full">
                    <InputNumber
                        id="updateFrequency"
                        value={currentConfig.updateFrequency as number}
                        onValueChange={handleConfigChange}
                    />
                    <span className="p-inputgroup-addon">s</span>
                </div>
            </div>
            <div className="col-2 flex flex-column mb-full">
                <label htmlFor="timeAxisStepSeconds">Time Axis Step</label>
                <div className="p-inputgroup w-full">
                    <InputNumber
                        id="timeAxisStepSeconds"
                        value={currentConfig.timeAxisStepSeconds as number}
                        onValueChange={handleConfigChange}
                    />
                    <span className="p-inputgroup-addon">s</span>
                </div>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="showDateSelection">Show Date Selection</label>
                <InputSwitch
                    id="showDateSelection"
                    checked={currentConfig.showDateSelection}
                    onChange={handleConfigChange}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="hideRefresh">Hide Refresh</label>
                <InputSwitch id="hideRefresh" checked={currentConfig.hideRefresh} onChange={handleConfigChange} />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="toggleTracesGrouping">Group traces</label>
                <InputSwitch
                    id="toggleTracesGrouping"
                    checked={currentConfig.toggleTracesGrouping}
                    onChange={handleConfigChange}
                />
            </div>
            <div className="gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="loadConf">Load Conf</label>
                <InputSwitch id="loadConf" checked={currentConfig.loadConf !== undefined} onChange={handleLoadConf} />
            </div>
            {currentConfig.loadConf !== undefined && (
                <>
                    <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="lead">Lead</label>
                        <div className="p-inputgroup">
                            <InputNumber
                                id="lead"
                                value={currentConfig.loadConf.lead as number}
                                onValueChange={handleLoadConfChange}
                            />
                            <TimeUnitDropdown
                                id="leadUnit"
                                className="p-inputgroup-addon p-0"
                                value={currentConfig.loadConf.leadUnit}
                                onChange={handleLoadConfChange}
                            />
                        </div>
                    </div>
                    <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="lag">Lag</label>
                        <div className="p-inputgroup">
                            <InputNumber
                                id="lag"
                                value={currentConfig.loadConf.lag as number}
                                onValueChange={handleLoadConfChange}
                            />
                            <TimeUnitDropdown
                                id="lagUnit"
                                className="p-inputgroup-addon p-0"
                                value={currentConfig.loadConf.lagUnit}
                                onChange={handleLoadConfChange}
                            />
                        </div>
                    </div>
                    <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="alignUnit">Align Unit</label>
                        <TimeUnitDropdown
                            id="alignUnit"
                            className="w-full"
                            value={ currentConfig.loadConf.alignUnit ? currentConfig.loadConf.alignUnit : ""}
                            onChange={handleLoadConfChange}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default DatasetLineChartEditor as FunctionComponent;
